// Adapted from our own Immersive Worlds WorldState.
// Source blob: 09bc584c691e5dc356434f773d561d086b587d57
// Core invariant retained: WORLD STATE != CAMERA STATE.

import { CAMERA_LEAK_KEYS, EXPERIENCE_MODE, PROPERTY_STATE, ENTITY_KIND } from './types.js';
import { EVENTS } from './event-bus.js';

export class WorldState {
  constructor({ store, bus }) {
    this.store = store;
    this.bus = bus;
    this.activeSpaceId = store.startSpaceId;
    this.arrivalAnchorId = store.require(store.startSpaceId).defaultSpawnAnchorId || null;
    this.focusedEntityId = null;
    this.mode = EXPERIENCE_MODE.EXPLORE;
    this.activeRouteId = null;

    this.visitedSpaceIds = new Set([store.startSpaceId]);
    this.visitedEntityIds = new Set();
    this.savedPropertyIds = new Set();
    this.rejectedPropertyIds = new Set();
    this.comparePropertyIds = new Set();
    this.visitRequestedPropertyIds = new Set();
    this.propertyStates = new Map(store.properties().map((property) => [property.id, PROPERTY_STATE.UNSEEN]));

    this.filters = {};
    this.buyerProfile = null;
    this.revision = 0;
  }

  _changed(reason, detail = {}) {
    this.revision += 1;
    this.bus.emit(EVENTS.WORLD_STATE_CHANGED, { reason, revision: this.revision, ...detail });
  }

  setMode(mode) {
    if (!Object.values(EXPERIENCE_MODE).includes(mode)) throw new Error(`[WCB] invalid mode ${mode}`);
    if (this.mode === mode) return;
    const previous = this.mode;
    this.mode = mode;
    this.bus.emit(EVENTS.MODE_CHANGED, { previous, mode });
    this._changed('mode', { previous, mode });
  }

  enterSpace(spaceId, arrivalAnchorId = null) {
    this.store.require(spaceId);
    const previousSpaceId = this.activeSpaceId;
    if (previousSpaceId && previousSpaceId !== spaceId) this.bus.emit(EVENTS.SPACE_LEFT, { spaceId: previousSpaceId });
    this.activeSpaceId = spaceId;
    this.arrivalAnchorId = arrivalAnchorId || this.store.require(spaceId).defaultSpawnAnchorId || null;
    this.visitedSpaceIds.add(spaceId);
    this.bus.emit(EVENTS.SPACE_ENTERED, { spaceId, previousSpaceId, arrivalAnchorId: this.arrivalAnchorId });
    this._changed('space', { spaceId, previousSpaceId });
  }

  focus(entityId) {
    if (entityId !== null) this.store.require(entityId);
    if (this.focusedEntityId === entityId) return;
    const previous = this.focusedEntityId;
    if (previous) this.bus.emit(EVENTS.ENTITY_FOCUS_LEFT, { entityId: previous });
    this.focusedEntityId = entityId;
    if (entityId) {
      this.visitedEntityIds.add(entityId);
      const entity = this.store.require(entityId);
      if (entity.kind === ENTITY_KIND.PROPERTY) {
        const current = this.propertyStates.get(entityId);
        if (current === PROPERTY_STATE.UNSEEN) this.setPropertyState(entityId, PROPERTY_STATE.SEEN);
        if (this.propertyStates.get(entityId) === PROPERTY_STATE.SEEN) this.setPropertyState(entityId, PROPERTY_STATE.OPENED);
      }
      this.bus.emit(EVENTS.ENTITY_FOCUSED, { entityId, mode: this.mode });
    }
    this._changed('focus', { entityId, previous });
  }

  setPropertyState(propertyId, state) {
    const entity = this.store.require(propertyId);
    if (entity.kind !== ENTITY_KIND.PROPERTY) throw new Error(`[WCB] ${propertyId} is not a property`);
    if (!Object.values(PROPERTY_STATE).includes(state)) throw new Error(`[WCB] invalid property state ${state}`);
    const previous = this.propertyStates.get(propertyId);
    if (previous === state) return;
    this.propertyStates.set(propertyId, state);
    this.bus.emit(EVENTS.PROPERTY_STATE_CHANGED, { propertyId, previous, state });
    this._changed('property-state', { propertyId, previous, state });
  }

  saveProperty(propertyId) {
    this._requireProperty(propertyId);
    this.rejectedPropertyIds.delete(propertyId);
    this.savedPropertyIds.add(propertyId);
    this.setPropertyState(propertyId, PROPERTY_STATE.SHORTLISTED);
    this.bus.emit(EVENTS.PROPERTY_SAVED, { propertyId });
  }

  rejectProperty(propertyId) {
    this._requireProperty(propertyId);
    this.savedPropertyIds.delete(propertyId);
    this.comparePropertyIds.delete(propertyId);
    this.rejectedPropertyIds.add(propertyId);
    this.setPropertyState(propertyId, PROPERTY_STATE.REJECTED);
    this.bus.emit(EVENTS.PROPERTY_REJECTED, { propertyId });
  }

  toggleCompare(propertyId) {
    this._requireProperty(propertyId);
    if (this.comparePropertyIds.has(propertyId)) this.comparePropertyIds.delete(propertyId);
    else this.comparePropertyIds.add(propertyId);
    if (this.comparePropertyIds.has(propertyId)) this.setPropertyState(propertyId, PROPERTY_STATE.COMPARE);
    this.bus.emit(EVENTS.PROPERTY_COMPARE_CHANGED, { propertyId, compare: [...this.comparePropertyIds] });
    this._changed('compare', { propertyId });
  }

  requestVisit(propertyId) {
    this._requireProperty(propertyId);
    this.visitRequestedPropertyIds.add(propertyId);
    this.setPropertyState(propertyId, PROPERTY_STATE.VISIT_REQUESTED);
    this.bus.emit(EVENTS.VISIT_REQUESTED, { propertyId });
  }

  setBuyerProfile(profile) {
    this.buyerProfile = profile ? structuredClone(profile) : null;
    this.bus.emit(EVENTS.BUYER_PROFILE_CHANGED, { buyerProfile: this.buyerProfile });
    this._changed('buyer-profile');
  }

  setFilters(filters = {}) {
    this.filters = structuredClone(filters);
    this.bus.emit(EVENTS.FILTERS_CHANGED, { filters: this.filters });
    this._changed('filters');
  }

  _requireProperty(propertyId) {
    const entity = this.store.require(propertyId);
    if (entity.kind !== ENTITY_KIND.PROPERTY) throw new Error(`[WCB] ${propertyId} is not a property`);
    return entity;
  }

  snapshot() {
    return {
      worldId: this.store.id,
      revision: this.revision,
      mode: this.mode,
      activeSpaceId: this.activeSpaceId,
      arrivalAnchorId: this.arrivalAnchorId,
      focusedEntityId: this.focusedEntityId,
      activeRouteId: this.activeRouteId,
      visitedSpaceIds: [...this.visitedSpaceIds].sort(),
      visitedEntityIds: [...this.visitedEntityIds].sort(),
      savedPropertyIds: [...this.savedPropertyIds].sort(),
      rejectedPropertyIds: [...this.rejectedPropertyIds].sort(),
      comparePropertyIds: [...this.comparePropertyIds].sort(),
      visitRequestedPropertyIds: [...this.visitRequestedPropertyIds].sort(),
      propertyStates: Object.fromEntries([...this.propertyStates.entries()].sort()),
      filters: structuredClone(this.filters),
      buyerProfile: this.buyerProfile ? structuredClone(this.buyerProfile) : null
    };
  }

  assertNoCameraState() {
    const leaked = Object.keys(this).filter((key) => CAMERA_LEAK_KEYS.includes(key));
    if (leaked.length) throw new Error(`[WCB] World State leaked camera ownership: ${leaked.join(', ')}`);
    return true;
  }
}
